import { useMemo, useState } from 'react';
import { STAGES, type Stage } from '@ouradent/shared';
import { useSession } from '../../auth/SessionProvider';
import {
  extractCall,
  queryClient,
  sendLeadEmail,
  useBoard,
  useCalendarConnection,
  useCalls,
} from '../../lib/data';
import { env } from '../../lib/env';
import { supabase } from '../../lib/supabase';
import { at, fDT, fLong, fromD, fromDT, toD, toDT } from '../../lib/format';
import type { Call } from '../../lib/types';
import { Icon } from '../../ui/icons';
import { CloseX, OwnerOptions, useDrawer, useToast } from '../../ui/kit';

type Row = { on: boolean; text: string; owner: string; due: string; evidence?: string | undefined };

export function CallDrawer({ id }: { id: string }) {
  const calls = useCalls();
  const call = calls.data?.find((c) => c.id === id);
  if (!call) {
    return (
      <div className="dhead">
        <h2 id="dtitle">{calls.isLoading ? 'Loading…' : 'Call not found'}</h2>
        <CloseX />
      </div>
    );
  }
  // A new AI draft remounts the review so its rows start from the draft.
  return call.status === 'done' ? (
    <DoneCall call={call} />
  ) : (
    <ReviewCall key={call.extracted_at ?? 'manual'} call={call} />
  );
}

function Head({ call }: { call: Call }) {
  return (
    <div className="dhead">
      <span className="vid" style={{ width: 52, height: 52 }}>
        <Icon name="video" />
      </span>
      <div>
        <h2 id="dtitle">{call.title}</h2>
        <div className="sm">
          {fDT(call.started_at)}
          {call.duration_min ? `, ${call.duration_min} min` : ''}
          {call.attendee.name ? `, with ${call.attendee.name}` : ''}
          {call.report_url && (
            <>
              {' · '}
              <a href={call.report_url} target="_blank" rel="noreferrer">
                Open in Read.ai
              </a>
            </>
          )}
        </div>
      </div>
      <CloseX />
    </div>
  );
}

function DoneCall({ call }: { call: Call }) {
  const { open } = useDrawer();
  const board = useBoard();
  const lead = board.data?.find((l) => l.id === call.lead_id);
  return (
    <>
      <Head call={call} />
      <div className="dbody">
        <div className="blk">
          <h3>
            <span className="lbl g">Summary</span>
          </h3>
          <div className="panel">
            <p>{call.summary}</p>
          </div>
        </div>
        {call.promises.filter((p) => p.on).length > 0 && (
          <div className="blk">
            <h3>
              <span className="lbl p">Tasks created</span>
            </h3>
            <div className="panel">
              <ul>
                {call.promises
                  .filter((p) => p.on)
                  .map((p, i) => (
                    <li key={i}>{p.text}</li>
                  ))}
              </ul>
            </div>
          </div>
        )}
        {lead && (
          <button className="btn" onClick={() => open({ type: 'lead', id: lead.id })}>
            Open {lead.clinic}
          </button>
        )}
      </div>
    </>
  );
}

function ReviewCall({ call }: { call: Call }) {
  const { me } = useSession();
  const { close, open } = useDrawer();
  const toast = useToast();
  const board = useBoard();
  const lead = board.data?.find((l) => l.id === call.lead_id);
  const active = (board.data ?? []).filter(
    (l) => !['won', 'lost', 'disq', 'nurture'].includes(l.stage),
  );

  const [rows, setRows] = useState<Row[]>(
    (call.promises ?? []).map((p) => ({
      on: p.on,
      text: p.text,
      owner: p.owner ?? me?.id ?? '',
      due: toD(p.due),
      evidence: p.evidence,
    })),
  );
  const [nextText, setNextText] = useState(call.next_step?.text ?? '');
  const [nextDue, setNextDue] = useState(call.next_step ? toDT(call.next_step.due) : '');
  const [nextOwner, setNextOwner] = useState(call.next_step?.owner ?? me?.id ?? '');
  const [dm, setDm] = useState<'yes' | 'no'>(call.dm === false ? 'no' : 'yes');
  const [stage, setStage] = useState<Stage>(call.suggested_stage ?? lead?.stage ?? 'call1');
  const [linkSel, setLinkSel] = useState('');
  const [subject, setSubject] = useState('Recap of our call and next steps');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const recap = useMemo(() => {
    const lines = [
      `Hi ${call.attendee.salutation ?? call.attendee.name ?? ''},`,
      '',
      'Thanks for your time today. A short recap of our call:',
      '',
    ];
    for (const p of call.recap_points ?? []) lines.push(`- ${p}`);
    const kept = rows.filter((r) => r.on && r.text.trim());
    if (kept.length) {
      lines.push('', 'What you’ll get from us:');
      for (const r of kept) lines.push(`- ${r.text}`);
    }
    if (nextText.trim()) {
      const d = fromDT(nextDue);
      lines.push('', `Next step: ${nextText.trim()}${d ? `, ${fLong(d)}` : ''}.`);
    }
    if (dm === 'no')
      lines.push('It would be great to have everyone involved in the decision on that call.');
    lines.push('', 'Best regards,', me?.fullName ?? '', 'Ouradent', 'ahoy@ouradent.com');
    return lines.join('\n');
  }, [call, rows, nextText, nextDue, dm, me?.fullName]);
  const [body, setBody] = useState<string | null>(null);
  const conn = useCalendarConnection();
  const [sendNow, setSendNow] = useState(false);
  const canMail = Boolean(conn.data?.gmail && (call.attendee.email || lead?.email));

  const patch = (i: number, p: Partial<Row>) =>
    setRows(rows.map((r, k) => (k === i ? { ...r, ...p } : r)));

  async function link(leadId: string) {
    const { error } = await supabase.rpc('link_call', { p_call: call.id, p_lead: leadId });
    if (error) return setErr(error.message);
    await queryClient.invalidateQueries();
    toast('Call linked');
  }

  async function dismiss() {
    setBusy(true);
    const { error } = await supabase.rpc('dismiss_call', { p_call: call.id });
    setBusy(false);
    if (error) return setErr(error.message);
    await queryClient.invalidateQueries();
    close();
    toast('Removed from Call reviews');
  }

  async function confirm() {
    if (!call.lead_id) return setErr('Link this call to a lead first.');
    const closes = ['won', 'lost', 'disq', 'nurture'].includes(stage);
    const due = fromDT(nextDue);
    if (!closes && (!nextText.trim() || !due)) {
      return setErr('Every call ends with a next step. Add what happens next and when.');
    }
    setBusy(true);
    setErr('');
    const promises = rows
      .filter((r) => r.on && r.text.trim())
      .map((r) => ({
        text: r.text.trim(),
        owner: r.owner || null,
        due: fromD(r.due) ?? at(1),
        on: true,
      }));
    const { error } = await supabase.rpc('confirm_call', {
      p_call: call.id,
      p_promises: promises,
      p_next_text: nextText.trim() || null,
      p_next_due: due,
      p_next_owner: nextOwner || null,
      p_dm: dm === 'yes',
      p_stage: stage,
      p_recap_subject: subject,
    });
    if (error) {
      setBusy(false);
      return setErr(error.message);
    }
    let note = '';
    if (sendNow && canMail) {
      try {
        await sendLeadEmail(call.lead_id, subject, body ?? recap);
        note = ' The recap was emailed.';
      } catch (e) {
        note = ` The recap was not sent: ${e instanceof Error ? e.message : String(e)}`;
      }
    }
    setBusy(false);
    await queryClient.invalidateQueries();
    close();
    toast(`Confirmed. ${promises.length} task${promises.length === 1 ? '' : 's'} created.${note}`);
  }

  return (
    <>
      <Head call={call} />
      <div className="dbody">
        <div className="blk">
          <h3>
            <span className="lbl g">Lead</span>
          </h3>
          {lead ? (
            <div
              className="panel"
              style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
            >
              <span className="mk">{lead.market}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{lead.clinic}</b>
                <div style={{ color: 'var(--muted)', fontSize: 14 }}>
                  {call.attendee.email
                    ? `Matched by attendee email ${call.attendee.email}`
                    : lead.person}
                </div>
              </div>
              <button className="btn sm" onClick={() => open({ type: 'lead', id: lead.id })}>
                Open
              </button>
            </div>
          ) : (
            <div className="panel">
              <p style={{ color: 'var(--red)', fontWeight: 600 }}>
                {call.attendee.email
                  ? `No lead matches ${call.attendee.email}.`
                  : 'This call is not linked to a lead.'}
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select
                  aria-label="Link to a lead"
                  style={{ flex: '1 1 220px' }}
                  value={linkSel}
                  onChange={(e) => setLinkSel(e.target.value)}
                >
                  <option value="">Link to an existing lead…</option>
                  {active.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.clinic} ({l.person})
                    </option>
                  ))}
                </select>
                <button
                  className="btn sm primary"
                  disabled={!linkSel}
                  onClick={() => void link(linkSel)}
                >
                  Link
                </button>
                <button
                  className="btn sm"
                  onClick={() =>
                    open({
                      type: 'newLead',
                      fromCall: {
                        id: call.id,
                        person: call.attendee.name,
                        email: call.attendee.email,
                      },
                    })
                  }
                >
                  Add as new lead
                </button>
              </div>
            </div>
          )}
        </div>

        {env.aiExtraction && <AiDraft call={call} />}

        <div className="blk">
          <h3>
            <span className="lbl g">Summary</span>
          </h3>
          <div className="panel">
            <p>{call.summary ?? 'No summary stored for this call.'}</p>
            {call.objections.length > 0 && (
              <p style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {call.objections.map((o) => (
                  <span key={o} className="pill sm or">
                    {o}
                  </span>
                ))}
              </p>
            )}
          </div>
        </div>

        <div className="blk">
          <h3>
            <span className="lbl p">What we promised to send</span>
          </h3>
          <p style={{ margin: '-2px 0 10px', color: 'var(--muted)', fontSize: 14 }}>
            Each one becomes a task with an owner and a deadline.
            {call.report_url &&
              ' These are Read.ai’s action items: ours are switched on, the clinic’s own are off.'}
          </p>
          <div className="panel">
            {rows.map((r, i) => (
              <div key={i}>
                <div className={`prow ${r.on ? '' : 'off'}`}>
                  <input
                    type="checkbox"
                    checked={r.on}
                    aria-label="Include"
                    onChange={(e) => patch(i, { on: e.target.checked })}
                  />
                  <input
                    type="text"
                    value={r.text}
                    aria-label="Promise"
                    onChange={(e) => patch(i, { text: e.target.value })}
                  />
                  <select
                    aria-label="Owner"
                    value={r.owner}
                    onChange={(e) => patch(i, { owner: e.target.value })}
                  >
                    <OwnerOptions />
                  </select>
                  <input
                    type="date"
                    value={r.due}
                    aria-label="Due"
                    onChange={(e) => patch(i, { due: e.target.value })}
                  />
                  <button
                    className="del"
                    aria-label="Remove"
                    onClick={() => setRows(rows.filter((_, k) => k !== i))}
                  >
                    ×
                  </button>
                </div>
                {r.evidence && <q className="evid">{r.evidence}</q>}
              </div>
            ))}
            {!rows.length && (
              <div className="empty" style={{ padding: 0 }}>
                Nothing promised yet.
              </div>
            )}
          </div>
          <button
            className="btn sm"
            style={{ marginTop: 10 }}
            onClick={() =>
              setRows([...rows, { on: true, text: '', owner: me?.id ?? '', due: toD(at(1)) }])
            }
          >
            Add a promise
          </button>
        </div>

        <div className="blk">
          <section className="pass">
            <span className="lbl t">Next step</span>
            <div className="passedit" style={{ marginTop: 16 }}>
              <label className="field full">
                <span>What happens next</span>
                <input type="text" value={nextText} onChange={(e) => setNextText(e.target.value)} />
              </label>
              <label className="field">
                <span>When</span>
                <input
                  type="datetime-local"
                  value={nextDue}
                  onChange={(e) => setNextDue(e.target.value)}
                />
              </label>
              <label className="field">
                <span>Who</span>
                <select
                  aria-label="Who"
                  value={nextOwner}
                  onChange={(e) => setNextOwner(e.target.value)}
                >
                  <OwnerOptions />
                </select>
              </label>
              <label className="field">
                <span>Decision-maker on the call?</span>
                <select
                  aria-label="Decision-maker on the call?"
                  value={dm}
                  onChange={(e) => setDm(e.target.value as 'yes' | 'no')}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="field">
                <span>Move lead to</span>
                <select
                  aria-label="Move lead to"
                  value={stage}
                  onChange={(e) => setStage(e.target.value as Stage)}
                >
                  {STAGES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {call.next_step?.evidence && nextText === call.next_step.text && (
              <q className="evid">{call.next_step.evidence}</q>
            )}
            {dm === 'no' && (
              <div className="hint">
                The decision-maker wasn’t on this call. Make sure they join the next one.
              </div>
            )}
          </section>
        </div>

        <div className="blk">
          <h3>
            <span className="lbl g">Recap email draft</span>
          </h3>
          <div className="mail">
            <div className="mh">
              <span>From</span>
              <div>ahoy@ouradent.com</div>
              <span>To</span>
              <div>{call.attendee.email ?? 'no email on this call'}</div>
              <span>Subject</span>
              <input
                type="text"
                aria-label="Email subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <textarea
              aria-label="Email body"
              value={body ?? recap}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          {canMail ? (
            <label
              className="field"
              style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}
            >
              <input
                type="checkbox"
                checked={sendNow}
                onChange={(e) => setSendNow(e.target.checked)}
                aria-label="Send the recap by email"
              />
              <span>
                Send this email to {call.attendee.email ?? lead?.email} from ahoy@ouradent.com when
                I confirm
              </span>
            </label>
          ) : (
            <p style={{ color: 'var(--muted)', fontSize: 14 }}>
              {conn.data?.gmail === false
                ? 'Gmail is not allowed yet, so the draft is kept for you to copy. An owner reconnects Google on Calendar.'
                : 'There is no email address on this call or lead, so the draft is kept for you to copy.'}
            </p>
          )}
          {body !== null && (
            <button className="btn sm ghost" onClick={() => setBody(null)}>
              Rewrite from the promises and next step
            </button>
          )}
        </div>
      </div>
      <div className="dfoot">
        {err && (
          <p className="err" role="alert">
            {err}
          </p>
        )}
        <button className="btn primary big" onClick={() => void confirm()} disabled={busy}>
          Confirm call <Icon name="arrow" />
        </button>
        <button className="btn ghost" onClick={close}>
          Later
        </button>
        <button className="btn ghost" onClick={() => void dismiss()} disabled={busy}>
          Not a sales call
        </button>
      </div>
    </>
  );
}

function AiDraft({ call }: { call: Call }) {
  const toast = useToast();
  const [editing, setEditing] = useState(!call.extracted_at);
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    setErr('');
    try {
      const r = await extractCall(call.id, text);
      toast(
        `AI drafted ${r.promises} promise${r.promises === 1 ? '' : 's'}.` +
          (r.droppedWithoutEvidence
            ? ` ${r.droppedWithoutEvidence} left out, no matching quote in the text.`
            : ''),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <div className="blk">
        <div className="panel aidone">
          <Icon name="spark" />
          <p>
            Drafted by AI from the pasted text. Check every item before you confirm, the quotes show
            where each promise was said.
          </p>
          <button className="btn sm ghost" onClick={() => setEditing(true)}>
            Paste again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blk">
      <h3>
        <span className="lbl p">Draft with AI</span>
      </h3>
      <div className="panel">
        <label className="field full">
          <span>Read.ai summary or transcript</span>
          <textarea
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste the summary, the transcript or both."
          />
        </label>
        <p className="aihint">
          AI suggests the promises, the next step and the recap. Nothing is sent to the prospect.
          {call.extracted_at ? ' This replaces the current draft below.' : ''}
        </p>
        {err && (
          <p className="err" role="alert">
            {err}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="btn sm primary"
            disabled={busy || text.trim().length < 40}
            onClick={() => void run()}
          >
            {busy ? 'Drafting…' : 'Draft the review'}
          </button>
          {call.extracted_at && (
            <button className="btn sm ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
